import React, { createContext, useContext, useState, ReactNode } from 'react';
import { User, LoginCredentials, SignupCredentials, AuthContextType } from '../types/auth';
import { apiService, ApiResponse, LoginResponse, SignupResponse } from '../services/api';
import { useMsal } from '@azure/msal-react';
import { loginRequest, redirectRequest, jiraSSOConfig } from '../config/msalConfig';
import { navigationService } from '../services/navigationService';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true); // Track if auth initialization is in progress
  const [error, setError] = useState<string | null>(null);
  const { instance, accounts } = useMsal();

  const clearError = () => setError(null);

  // Debug: Track user state changes
  React.useEffect(() => {
    console.log('User state changed:', { user, timestamp: new Date().toISOString() });
  }, [user]);

  // Helper function to safely get error message
  const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'string') {
      return error;
    }
    if (error && typeof error === 'object' && 'message' in error) {
      return String(error.message);
    }
    return 'An unknown error occurred';
  };

  // const login = async (credentials: LoginCredentials): Promise<void> => {
  //   setIsLoading(true);
  //   setError(null);
    
  //   try {
  //     // Use Azure AD SSO for authentication
  //     const loginResponse = await instance.loginPopup({
  //       ...loginRequest,
  //       redirectUri: window.location.origin + '/',
  //     });
      
  //     if (loginResponse.account) {
  //       // Get access token for Jira SSO
  //       const tokenRequest = {
  //         scopes: ['openid', 'profile', 'email'],
  //         account: loginResponse.account,
  //       };
        
  //       const tokenResponse = await instance.acquireTokenSilent(tokenRequest);
        
  //       // Create user object from Azure AD response
  //       const user: User = {
  //         id: loginResponse.account.homeAccountId,
  //         email: loginResponse.account.username,
  //         name: loginResponse.account.name || loginResponse.account.username.split('@')[0],
  //       };

  //       setUser(user);
        
  //       // Store tokens and user data
  //       if (credentials.keepLoggedIn) {
  //         localStorage.setItem('azureAccessToken', tokenResponse.accessToken);
  //         localStorage.setItem('azureIdToken', tokenResponse.idToken);
  //         localStorage.setItem('user', JSON.stringify(user));
  //       }

  //       // Redirect to Jira with SSO token
  //       const jiraSSOUrl = `${jiraSSOConfig.jiraSSOUrl}?SAMLResponse=${encodeURIComponent(tokenResponse.idToken)}`;
  //       window.location.href = jiraSSOUrl;
        
  //       console.log('Azure AD SSO login successful, redirecting to Jira');
  //     }
  //   } catch (error) {
  //     console.error('Azure AD SSO login failed:', error);
  //     const errorMessage = getErrorMessage(error);
  //     if (!errorMessage) {
  //       setError('SSO login failed. Please try again.');
  //     } else {
  //       setError(errorMessage);
  //     }
  //     throw error;
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const login = async (credentials: LoginCredentials): Promise<void> => {
    // console.log('=== AUTH CONTEXT LOGIN FUNCTION STARTED ===');
    // console.log('Login credentials:', credentials);
    // console.log('Current user state before login:', user);
    setIsLoading(true);
    setError(null);
    const user_email_entered = credentials.email
    
    const interactivePopup = {
      ...loginRequest,
      loginHint: user_email_entered,           // <-- prefill username
      prompt: "login",                // <-- go straight to password
    } as typeof loginRequest;

    const interactiveRedirect = {
      ...redirectRequest,
      loginHint: user_email_entered,
      prompt: "login",
    } as typeof redirectRequest;

    try {
      // console.log('Starting Azure AD SSO login...');
      // console.log('Current window location:', window.location.href);
      // console.log('Current origin:', window.location.origin);
      
      // Check if MSAL is initialized
      console.log('MSAL configuration check:', !!instance.getConfiguration());
      if (!instance.getConfiguration()) {
        console.log('MSAL not initialized, initializing...');
        await instance.initialize();
        console.log('MSAL initialization completed');
      }
      
      // Check for existing accounts first
      const accounts = instance.getAllAccounts();
      // console.log('Existing accounts:', accounts.length);
      // console.log('Account details:', accounts.map(acc => ({ username: acc.username, homeAccountId: acc.homeAccountId })));
      // console.log('Current user state:', user);
      
      let loginResponse;
      
      if (accounts.length > 0) {
        // Try silent login first if account exists
        console.log('Attempting silent login with existing account...');
        try {
          const silentRequest = {
            scopes: ['openid', 'profile', 'email', 'https://graph.microsoft.com/User.Read'],
            account: accounts[0],
          };
          loginResponse = await instance.acquireTokenSilent(silentRequest);
          console.log('Silent login successful');
        } catch (silentError) {
          console.log('Silent login failed, trying popup login:', silentError);
          try {

            loginResponse = await instance.loginPopup(loginRequest);
            //loginResponse = await instance.loginPopup(interactivePopup);
            console.log('Popup login successful after silent failure');
          } catch (popupError) {
            console.error('Popup login also failed:', popupError);
            throw popupError;
          }
        }
      } else {
        // No existing accounts, try popup login first, fallback to redirect
        console.log('No existing accounts, trying popup login...');
        console.log('Login request config:', loginRequest);
        try {
          console.log('Attempting popup login...');
          
          loginResponse = await instance.loginPopup(loginRequest);
          //loginResponse = await instance.loginPopup(interactivePopup);
          console.log('Popup login successful:', loginResponse);
        } catch (popupError) {
          console.warn('Popup login failed, trying redirect login:', popupError);
          console.log('Popup error details:', {
            errorCode: (popupError as any).errorCode,
            errorMessage: (popupError as any).errorMessage,
            name: (popupError as any).name
          });
          // If popup fails, try redirect as fallback
          console.log('Attempting redirect login...');
          await instance.loginRedirect(redirectRequest);
          return; // Redirect will navigate away from the page
        }
      }
  
      if (loginResponse && loginResponse.account) {
        console.log('Login successful, account:', loginResponse.account.username);
        
        // Get tokens for your app/backend
        const tokenRequest = {
          scopes: ['openid', 'profile', 'email', 'https://graph.microsoft.com/User.Read'],
          account: loginResponse.account,
        };
        
        console.log('Token request config:', tokenRequest);
        let tokenResponse;
        try {
          console.log('Attempting silent token acquisition...');
          tokenResponse = await instance.acquireTokenSilent(tokenRequest);
          console.log('Token acquisition successful:', tokenResponse);
        } catch (silentError) {
          // If silent token acquisition fails, try interactive
          console.warn('Silent token acquisition failed, trying interactive:', silentError);
          console.log('Silent error details:', {
            errorCode: (silentError as any).errorCode,
            errorMessage: (silentError as any).errorMessage,
            name: (silentError as any).name
          });
          console.log('Attempting interactive token acquisition...');
          tokenResponse = await instance.acquireTokenPopup(tokenRequest);
          console.log('Interactive token acquisition successful:', tokenResponse);
        }
  
        // Build your app user
        const user: User = {
          id: loginResponse.account.homeAccountId,
          email: loginResponse.account.username,
          name: loginResponse.account.name || loginResponse.account.username.split('@')[0],
        };
        console.log('Creating user object:', user);
        
        // Persist user data first
        console.log('Persisting user data to localStorage...');
        localStorage.setItem('azureAccessToken', tokenResponse.accessToken);
        localStorage.setItem('azureIdToken', tokenResponse.idToken);
        localStorage.setItem('user', JSON.stringify(user));
        console.log('User data persisted to localStorage successfully');
        
        // Set user state after persistence
        console.log('Setting user state in React context...');
        setUser(user);
        console.log('User state set successfully');

        // Navigate to home page
        console.log('Attempting navigation to Home Page...');
        try {
          navigationService.navigateTo('/home');
          console.log('Navigation service successful');
        } catch (error) {
          console.error('Navigation service failed, using window.location:', error);
          window.location.assign('/home');
          console.log('Window location navigation initiated');
        }
  
        console.log('Azure AD SSO login successful, navigating to Home Page');
      } else {
        throw new Error('Azure sign-in did not return an account');
      }
    } catch (error) {
      console.error('Azure AD SSO login failed:', error);
      console.error('Full error object:', {
        name: (error as any).name,
        message: (error as any).message,
        errorCode: (error as any).errorCode,
        errorMessage: (error as any).errorMessage,
        stack: (error as any).stack
      });
      
      const errorMessage = getErrorMessage(error);
      console.log('Processed error message:', errorMessage);
      
      // Handle specific MSAL errors with more detailed messages
      if (errorMessage.includes('hash_empty_error') || errorMessage.includes('Authentication was interrupted')) {
        setError('Authentication was interrupted. This may be due to popup blockers or browser security settings. Please ensure popups are allowed and try again.');
      } else if (errorMessage.includes('popup_window_error') || errorMessage.includes('Popup was blocked')) {
        setError('Popup was blocked by your browser. Please allow popups for this site and try again.');
      } else if (errorMessage.includes('user_cancelled') || errorMessage.includes('User cancelled')) {
        setError('Sign-in was cancelled. Please try again.');
      } else if (errorMessage.includes('network_error') || errorMessage.includes('Network Error')) {
        setError('Network error occurred. Please check your internet connection and try again.');
      } else if (errorMessage.includes('timeout')) {
        setError('Authentication timed out. Please try again.');
      } else {
        setError(`SSO login failed: ${errorMessage || 'Unknown error occurred. Please try again.'}`);
      }
      throw error;
    } finally {
      setIsLoading(false);
      console.log('=== AUTH CONTEXT LOGIN FUNCTION ENDED ===');
    }
  };
  const signup = async (credentials: SignupCredentials): Promise<void> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response: ApiResponse<SignupResponse> = await apiService.signup({
        email: credentials.email,
        name: credentials.name,
        organisation: credentials.organisation,
      });

      if (response.error) {
        setError(response.error);
        throw new Error(response.error);
      }

      if (response.data) {
        // For signup, we don't automatically log the user in
        // They need to login separately as per the backend design
        console.log('Signup successful:', response.data.message);
        
        // You might want to show a success message and redirect to login
        // For now, we'll just clear any existing user
        setUser(null);
      }
    } catch (error) {
      console.error('Signup failed:', error);
      const errorMessage = getErrorMessage(error);
      if (!errorMessage) {
        setError('Signup failed. Please try again.');
      } else {
        setError(errorMessage);
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithGoogle = async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // TODO: Implement Google OAuth integration
      // For now, we'll show an error
      setError('Google login not yet implemented');
      throw new Error('Google login not yet implemented');
    } catch (error) {
      console.error('Google login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    console.log('Starting logout process...');
    try {
      // Clear local state first
      console.log('Clearing local state...');
      setUser(null);
      setError(null);
      
      // Clear localStorage
      console.log('Clearing localStorage...');
      localStorage.removeItem('azureAccessToken');
      localStorage.removeItem('azureIdToken');
      localStorage.removeItem('user');
      
      // Clear sessionStorage (MSAL cache)
      console.log('Clearing sessionStorage...');
      sessionStorage.clear();
      
      // Logout from Azure AD
      console.log('Available accounts for logout:', accounts.length);
      if (accounts.length > 0) {
        console.log('Performing Azure AD logout...');
        try {
          await instance.logoutPopup({
            account: accounts[0],
            postLogoutRedirectUri: window.location.origin,
          });
          console.log('Azure AD logout successful');
        } catch (popupError) {
          console.warn('Popup logout failed, trying redirect logout:', popupError);
          // Fallback to redirect logout
          await instance.logoutRedirect({
            account: accounts[0],
            postLogoutRedirectUri: window.location.origin,
          });
        }
      } else {
        console.log('No accounts to logout from');
      }
      
      // Clear MSAL cache completely
      console.log('Clearing MSAL cache...');
      instance.clearCache();
      
      console.log('Logout process completed');
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if logout fails, ensure local state is cleared
      setUser(null);
      setError(null);
      localStorage.clear();
      sessionStorage.clear();
    }
  };

  // Check for existing user on mount and handle redirect responses
  React.useEffect(() => {
    const initializeAuth = async () => {
      try {
        console.log('Initializing authentication...');
        
        // First, handle any redirect response
        console.log('Handling redirect response...');
        const response = await instance.handleRedirectPromise();
        console.log('Redirect response:', response);
        
        if (response && response.account) {
          console.log('Redirect login successful:', response.account.username);
          
          // Get tokens
          const tokenRequest = {
            scopes: ['openid', 'profile', 'email', 'https://graph.microsoft.com/User.Read'],
            account: response.account,
          };
          
          let tokenResponse;
          try {
            tokenResponse = await instance.acquireTokenSilent(tokenRequest);
            console.log('Token acquired silently');
          } catch (silentError) {
            console.log('Silent token acquisition failed, trying popup:', silentError);
            tokenResponse = await instance.acquireTokenPopup(tokenRequest);
          }
          
          // Create user object
          const user: User = {
            id: response.account.homeAccountId,
            email: response.account.username,
            name: response.account.name || response.account.username.split('@')[0],
          };
          
          // Persist user data first
          localStorage.setItem('azureAccessToken', tokenResponse.accessToken);
          localStorage.setItem('azureIdToken', tokenResponse.idToken);
          localStorage.setItem('user', JSON.stringify(user));
          console.log('User data persisted from redirect response');
          
          // Set user state after persistence
          setUser(user);
          
          // Navigate to home page
          try {
            navigationService.navigateTo('/home');
          } catch (error) {
            console.error('Navigation service failed, using window.location:', error);
            window.location.assign('/home');
          }
          
          return; // Exit early if redirect login was successful
        }
        
        // If no redirect response, check for stored user
        console.log('No redirect response detected, checking stored user...');
        const storedUser = localStorage.getItem('user');
        const storedToken = localStorage.getItem('azureIdToken');
        
        console.log('Checking stored user:', { storedUser: !!storedUser, storedToken: !!storedToken });
        console.log('Stored user data:', storedUser);
        
        if (storedUser && storedToken) {
          try {
            const user = JSON.parse(storedUser);
            console.log('Restoring user from storage:', user);
            
            // Verify the token is still valid by checking MSAL accounts
            const accounts = instance.getAllAccounts();
            const matchingAccount = accounts.find(acc => acc.homeAccountId === user.id);
            
            if (matchingAccount) {
              setUser(user);
              console.log('User restored from storage successfully');
            } else {
              console.log('Stored user account not found in MSAL, clearing storage');
              localStorage.removeItem('user');
              localStorage.removeItem('azureIdToken');
              localStorage.removeItem('azureAccessToken');
            }
          } catch (error) {
            console.error('Failed to parse stored user:', error);
            localStorage.removeItem('user');
            localStorage.removeItem('azureIdToken');
            localStorage.removeItem('azureAccessToken');
          }
        } else {
          console.log('No stored user found, user will need to login');
        }
        
      } catch (error) {
        console.error('Error during auth initialization:', error);
        // Clear any corrupted data
        localStorage.removeItem('user');
        localStorage.removeItem('azureIdToken');
        localStorage.removeItem('azureAccessToken');
      } finally {
        // Always mark initialization as complete
        console.log('Auth initialization completed');
        setIsInitializing(false);
      }
    };

    initializeAuth();
  }, [instance]);

  const value: AuthContextType = {
    user,
    login,
    signup,
    loginWithGoogle,
    logout,
    isLoading,
    isInitializing,
    error,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
